/**
 * Skeleton — shimmering placeholders for loading states.
 *
 * Drop-in replacements that match the shape of real content (TournamentCard,
 * team/athlete rows, profile/admin sections) so layout doesn't shift when
 * data arrives. Pulse animation = `animate-pulse` on `bg-gray-200 rounded`.
 *
 * Usage in a list page (the three-state guard):
 *   if (loading) return <Skeleton.List shape="tournament-card" count={6} />;
 *   if (items.length === 0) return <EmptyState … />;
 *   return <ActualList items={items} />;
 */

interface BarProps {
  className?: string;
}

function Bar({ className = 'h-3 w-full' }: BarProps) {
  return <div className={`bg-gray-200 rounded animate-pulse ${className}`} data-testid="skeleton-bar" />;
}

function Circle({ className = 'w-12 h-12' }: BarProps) {
  return <div className={`bg-gray-200 rounded-full animate-pulse ${className}`} data-testid="skeleton-circle" />;
}

/**
 * Mirror of TournamentCard layout: 12×12 logo + flex-1 (title bar + meta + badges) + right-aligned date stack.
 */
function TournamentCard() {
  return (
    <div className="card p-4 sm:p-5 flex gap-4" data-testid="skeleton-tournament-card">
      <div className="w-12 h-12 rounded-xl bg-gray-200 animate-pulse shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <Bar className="h-4 w-2/3" />
        <Bar className="h-3 w-1/3" />
        <div className="flex gap-1.5 mt-1">
          <Bar className="h-3 w-16" />
          <Bar className="h-3 w-12" />
        </div>
      </div>
      <div className="text-right shrink-0 space-y-1.5">
        <Bar className="h-4 w-16 ml-auto" />
        <Bar className="h-3 w-10 ml-auto" />
      </div>
    </div>
  );
}

/**
 * Team card — mirrors the real Team grid item (TeamList.tsx): 12×12 rounded-xl
 * logo box + name bar + location bar + badge row. Sized to drop into a
 * 3-column grid cell without overflowing.
 */
function TeamRow() {
  return (
    <div className="card p-4 flex items-start gap-3" data-testid="skeleton-team-row">
      <div className="w-12 h-12 rounded-xl bg-gray-200 animate-pulse shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <Bar className="h-4 w-3/4" />
        <Bar className="h-3 w-1/2" />
        <div className="flex gap-1.5 pt-1">
          <Bar className="h-3 w-12" />
          <Bar className="h-3 w-10" />
        </div>
      </div>
    </div>
  );
}

/**
 * Athlete card — mirrors the real AthleteCard layout: round avatar + name
 * + meta line + sub-meta. Vertical stack so it fits a 3-column grid cell.
 */
function AthleteRow() {
  return (
    <div className="card p-4 flex items-start gap-3" data-testid="skeleton-athlete-row">
      <Circle className="w-12 h-12 shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <Bar className="h-4 w-2/3" />
        <Bar className="h-3 w-1/2" />
        <Bar className="h-3 w-1/3" />
      </div>
    </div>
  );
}

/**
 * Generic section card: title bar + 3 content bars. Used in Profile + Admin sub-panels.
 */
function Section({ lines = 3 }: { lines?: number }) {
  return (
    <div className="card p-4 sm:p-5 space-y-3" data-testid="skeleton-section">
      <Bar className="h-4 w-1/4" />
      <div className="space-y-2 pt-1">
        {Array.from({ length: lines }).map((_, i) => (
          <Bar key={i} className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
        ))}
      </div>
    </div>
  );
}

/**
 * Convenience renderer for a list of skeletons. `cols` matches the real
 * page's grid layout — Teams + Athletes use 3-column responsive grids,
 * Tournaments + Home use a single column.
 */
type ListShape = 'tournament-card' | 'team-row' | 'athlete-row' | 'section';

function List({ shape, count = 4, cols = 1 }: { shape: ListShape; count?: number; cols?: 1 | 2 | 3 }) {
  const Item = shape === 'tournament-card' ? TournamentCard
             : shape === 'team-row'        ? TeamRow
             : shape === 'athlete-row'     ? AthleteRow
             : Section;
  const layout = cols === 3 ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'
               : cols === 2 ? 'grid grid-cols-1 sm:grid-cols-2 gap-3'
               : 'space-y-3';
  return (
    <div className={layout} data-testid="skeleton-list">
      {Array.from({ length: count }).map((_, i) => <Item key={i} />)}
    </div>
  );
}

const Skeleton = { Bar, Circle, TournamentCard, TeamRow, AthleteRow, Section, List };
export default Skeleton;
